import dayjs, { type Dayjs } from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { ISO_DATE_FORMAT, parseToIsoDate } from '../../utils/dateFormat';

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

type CalendarSubView = 'day' | 'month' | 'year';

export type MonthCalendarPanelProps = {
  month: Dayjs;
  onMonthChange: (month: Dayjs) => void;
  /** Single-date selection */
  selected?: string;
  /** Range selection */
  rangeFrom?: string;
  rangeTo?: string;
  onDaySelect: (iso: string) => void;
  minDate?: string;
  maxDate?: string;
  /**
   * When true, the calendar title opens a year grid, then month grid, then days
   * (similar to MUI DatePicker year → month → day).
   */
  enableYmdViews?: boolean;
};

function isDisabled(iso: string, minDate?: string, maxDate?: string): boolean {
  if (minDate && iso < minDate) return true;
  if (maxDate && iso > maxDate) return true;
  return false;
}

function isInRange(iso: string, from?: string, to?: string): boolean {
  if (!from) return false;
  const end = to || from;
  const start = from <= end ? from : end;
  const stop = from <= end ? end : from;
  return iso >= start && iso <= stop;
}

function isRangeEdge(iso: string, from?: string, to?: string): boolean {
  if (!from) return false;
  if (!to) return iso === from;
  return iso === from || iso === to;
}

function yearBounds(minDate?: string, maxDate?: string): { minY: number; maxY: number } {
  const minY = minDate ? dayjs(minDate).year() : 1900;
  const maxY = maxDate ? dayjs(maxDate).year() : dayjs().year() + 20;
  return minY <= maxY ? { minY, maxY } : { minY: maxY, maxY: minY };
}

function isYearFullyOutsideRange(y: number, minDate?: string, maxDate?: string): boolean {
  const startIso = `${y}-01-01`;
  const endIso = `${y}-12-31`;
  if (minDate && endIso < minDate) return true;
  if (maxDate && startIso > maxDate) return true;
  return false;
}

function isMonthOutsideRange(y: number, monthIndex: number, minDate?: string, maxDate?: string): boolean {
  const startIso = dayjs().year(y).month(monthIndex).startOf('month').format(ISO_DATE_FORMAT);
  const endIso = dayjs().year(y).month(monthIndex).endOf('month').format(ISO_DATE_FORMAT);
  if (minDate && endIso < minDate) return true;
  if (maxDate && startIso > maxDate) return true;
  return false;
}

function alignYearBlockStart(y: number): number {
  return Math.floor(y / 16) * 16;
}

export default function MonthCalendarPanel({
  month,
  onMonthChange,
  selected,
  rangeFrom,
  rangeTo,
  onDaySelect,
  minDate,
  maxDate,
  enableYmdViews = false,
}: MonthCalendarPanelProps) {
  const todayIso = dayjs().format(ISO_DATE_FORMAT);
  const selectedIso = selected ? parseToIsoDate(selected) : '';
  const fromIso = rangeFrom ? parseToIsoDate(rangeFrom) : '';
  const toIso = rangeTo ? parseToIsoDate(rangeTo) : '';

  const { minY, maxY } = useMemo(() => yearBounds(minDate, maxDate), [minDate, maxDate]);

  const [subView, setSubView] = useState<CalendarSubView>('day');
  const [yearBlockStart, setYearBlockStart] = useState(() => alignYearBlockStart(month.year()));

  const anchorYear = month.year();
  const anchorMonth = month.month();
  useEffect(() => {
    setYearBlockStart(alignYearBlockStart(anchorYear));
  }, [anchorYear, anchorMonth]);

  const view: CalendarSubView = enableYmdViews ? subView : 'day';

  const weeks = useMemo(() => {
    const start = month.startOf('month').startOf('week');
    const rows: Dayjs[][] = [];
    let cursor = start;
    for (let w = 0; w < 6; w++) {
      const days: Dayjs[] = [];
      for (let d = 0; d < 7; d++) {
        days.push(cursor);
        cursor = cursor.add(1, 'day');
      }
      rows.push(days);
    }
    return rows;
  }, [month]);

  const prevYearBlockDisabled = yearBlockStart + 15 < minY;
  const nextYearBlockDisabled = yearBlockStart > maxY;

  const goPrevYearInMonthView = () => {
    const y = month.year() - 1;
    if (y < minY) return;
    onMonthChange(month.year(y));
  };

  const goNextYearInMonthView = () => {
    const y = month.year() + 1;
    if (y > maxY) return;
    onMonthChange(month.year(y));
  };

  const prevMonthDisabled =
    minDate != null &&
    month.startOf('month').subtract(1, 'month').endOf('month').format(ISO_DATE_FORMAT) < minDate;
  const nextMonthDisabled =
    maxDate != null && month.endOf('month').add(1, 'day').format(ISO_DATE_FORMAT) > maxDate;

  if (view === 'year') {
    const years = Array.from({ length: 16 }, (_, i) => yearBlockStart + i);

    return (
      <div className="cal-panel cal-panel--ym">
        <div className="cal-panel-hdr">
          <button
            type="button"
            className="cal-panel-nav"
            aria-label="Previous years"
            disabled={prevYearBlockDisabled}
            onClick={() => setYearBlockStart((s) => s - 16)}
          >
            ‹
          </button>
          <button
            type="button"
            className="cal-panel-title cal-panel-title-btn"
            aria-label="Choose month"
            onClick={() => setSubView('month')}
          >
            {month.format('MMMM YYYY')}
            <span className="cal-panel-title-caret" aria-hidden>
              ▼
            </span>
          </button>
          <button
            type="button"
            className="cal-panel-nav"
            aria-label="Next years"
            disabled={nextYearBlockDisabled}
            onClick={() => setYearBlockStart((s) => s + 16)}
          >
            ›
          </button>
        </div>

        <div className="cal-panel-ym-scroll" role="listbox" aria-label="Select year">
          <div className="cal-panel-year-grid">
            {years.map((y) => {
              if (isYearFullyOutsideRange(y, minDate, maxDate)) return null;
              const isCurrent = y === month.year();
              return (
                <button
                  key={y}
                  type="button"
                  role="option"
                  aria-selected={isCurrent}
                  className={['cal-panel-ym-cell', isCurrent ? 'is-current' : ''].filter(Boolean).join(' ')}
                  onClick={() => {
                    onMonthChange(month.year(y));
                    setSubView('month');
                  }}
                >
                  {y}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  if (view === 'month') {
    const y = month.year();
    const prevYearDisabled = y <= minY;
    const nextYearDisabled = y >= maxY;

    return (
      <div className="cal-panel cal-panel--ym">
        <div className="cal-panel-hdr">
          <button
            type="button"
            className="cal-panel-nav"
            aria-label="Previous year"
            disabled={prevYearDisabled}
            onClick={goPrevYearInMonthView}
          >
            ‹
          </button>
          <button
            type="button"
            className="cal-panel-title cal-panel-title-btn"
            aria-label="Choose year"
            onClick={() => setSubView('year')}
          >
            {month.format('YYYY')}
            <span className="cal-panel-title-caret" aria-hidden>
              ▼
            </span>
          </button>
          <button
            type="button"
            className="cal-panel-nav"
            aria-label="Next year"
            disabled={nextYearDisabled}
            onClick={goNextYearInMonthView}
          >
            ›
          </button>
        </div>

        <div className="cal-panel-month-grid" role="listbox" aria-label="Select month">
          {MONTH_LABELS.map((label, monthIndex) => {
            if (isMonthOutsideRange(y, monthIndex, minDate, maxDate)) return null;
            const isCurrent = monthIndex === month.month();
            return (
              <button
                key={label}
                type="button"
                role="option"
                aria-selected={isCurrent}
                className={['cal-panel-ym-cell cal-panel-month-cell', isCurrent ? 'is-current' : '']
                  .filter(Boolean)
                  .join(' ')}
                onClick={() => {
                  onMonthChange(month.month(monthIndex));
                  setSubView('day');
                }}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  const titleEl = enableYmdViews ? (
    <button
      type="button"
      className="cal-panel-title cal-panel-title-btn"
      aria-expanded={subView !== 'day'}
      aria-haspopup="dialog"
      aria-label="Choose year and month"
      onClick={() => setSubView('year')}
    >
      {month.format('MMM, YYYY')}
      <span className="cal-panel-title-caret" aria-hidden>
        ▼
      </span>
    </button>
  ) : (
    <span className="cal-panel-title">{month.format('MMM, YYYY')}</span>
  );

  return (
    <div className="cal-panel">
      <div className="cal-panel-hdr">
        <button
          type="button"
          className="cal-panel-nav"
          aria-label="Previous month"
          disabled={prevMonthDisabled}
          onClick={() => onMonthChange(month.subtract(1, 'month'))}
        >
          ‹
        </button>
        {titleEl}
        <button
          type="button"
          className="cal-panel-nav"
          aria-label="Next month"
          disabled={nextMonthDisabled}
          onClick={() => onMonthChange(month.add(1, 'month'))}
        >
          ›
        </button>
      </div>

      <div className="cal-panel-weekdays" aria-hidden>
        {WEEKDAYS.map((w) => (
          <span key={w} className="cal-panel-wd">
            {w}
          </span>
        ))}
      </div>

      <div className="cal-panel-grid">
        {weeks.map((week) =>
          week.map((d) => {
            const iso = d.format(ISO_DATE_FORMAT);
            const inMonth = d.month() === month.month();
            const disabled = isDisabled(iso, minDate, maxDate);
            const inRange = !disabled && isInRange(iso, fromIso, toIso);
            const isEdge = !disabled && isRangeEdge(iso, fromIso, toIso);
            const isSelected = !disabled && selectedIso === iso;
            const isToday = todayIso === iso;

            return (
              <button
                key={`${month.format('YYYY-MM')}-${iso}`}
                type="button"
                disabled={disabled}
                className={[
                  'cal-panel-day',
                  !inMonth ? 'is-outside' : '',
                  disabled ? 'is-disabled' : '',
                  inRange ? 'is-in-range' : '',
                  isEdge || isSelected ? 'is-selected' : '',
                  isToday ? 'is-today' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onClick={() => onDaySelect(iso)}
              >
                {d.date()}
              </button>
            );
          }),
        )}
      </div>
    </div>
  );
}
